import { DashboardUnreadMessage } from "../../pages/api/dashboard";
import Link from "next/link";
import { useRouter } from "next/router";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "next-i18next";
import { da, enUS, nb, sv } from "date-fns/locale";

interface UnreadMessagesProps {
  messages: DashboardUnreadMessage[];
}

const localeMap: Record<string, Locale> = {
  da: da,
  en: enUS,
  no: nb,
  sv: sv,
};

export default function UnreadMessages({ messages }: UnreadMessagesProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const dateLocale = localeMap[router.locale || "en"] || enUS;

  const handleRowClick = (senderId: string) => {
    router.push(`/chat?userId=${senderId}`);
  };

  return (
    <div className="card h-100">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          {t("dashboard.unansweredMessages")}{" "}
          {messages.length > 0 && (
            <span className="badge bg-danger">{messages.length}</span>
          )}
        </h6>
        <Link href="/chat" className="btn btn-sm btn-outline-primary">
          {t("dashboard.seeAll")}
        </Link>
      </div>
      <div className="card-body p-0">
        {messages.length === 0 ? (
          <p className="text-muted p-3 mb-0">{t("dashboard.noUnansweredMessages")}</p>
        ) : (
          <ul className="list-group list-group-flush">
            {messages.slice(0, 5).map((msg, index) => (
              <li
                key={index}
                className="list-group-item d-flex justify-content-between align-items-start list-group-item-action"
                onClick={() => handleRowClick(msg.senderId)}
                style={{ cursor: "pointer" }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleRowClick(msg.senderId);
                  }
                }}
              >
                <div className="ms-2 me-auto">
                  <div className="fw-bold">
                    {msg.senderName || msg.senderEmail}
                  </div>
                  <small className="text-muted">
                    {msg.text.length > 50
                      ? `${msg.text.substring(0, 50)}...`
                      : msg.text}
                  </small>
                </div>
                <small className="text-muted">
                  {formatDistanceToNow(new Date(msg.timestamp), {
                    addSuffix: true,
                    locale: dateLocale,
                  })}
                </small>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
